import { ReactNode, useMemo } from 'react';

import { TFunction } from 'i18next';
import {
  ShieldAlertIcon,
  ShieldCheckIcon,
  ShieldIcon,
  ShieldOffIcon,
  ShieldXIcon,
  WebhookIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { AuthStepKey } from '@/components/pages/Authentication/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import useDialogs from '@/hooks/useDialogs';
import useWebhooks from '@/hooks/useWebhooks';
import { cn } from '@/lib/utils';
import { getActionsSelector } from '@/redux/dialogs/selectors';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import {
  AuthDebugStep,
  AuthEventType,
  AuthStepType,
  AuthTerminalCause,
  AuthValidationMethod,
  AuthValidationResult,
  ContextChangeType,
  DebugStep,
} from '@/shared/types/tryIt';

interface AuthStepProps {
  step: DebugStep;
}

type Tone = 'neutral' | 'success' | 'warning' | 'destructive';

// Numeric proto enums use 0 as their UNSPECIFIED value, which the orchestrator's
// stripDefaultGRPCValues drops to `undefined` before the payload reaches the
// client. So in practice an UNSPECIFIED variant never arrives — these maps
// stay Partial<Record<>> to avoid carrying unreachable entries (and unreachable
// locale strings). The truthy gate at the call site makes the `undefined` case
// short-circuit before we index into the map.

// Per-cause exit copy. Each locale phrases the full sentence so we don't have
// to lowercase translated strings client-side (locale-fragile) or stitch
// fragments together. THRESHOLD_MET is success-only and goes through
// step_completed instead — it has no entry here.
const EXIT_TRANSLATION_KEY: Partial<Record<AuthTerminalCause, string>> = {
  [AuthTerminalCause.MAX_ATTEMPTS]: 'try_it.auth.exit_max_attempts',
  [AuthTerminalCause.TIMEOUT]: 'try_it.auth.exit_timeout',
  [AuthTerminalCause.SYSTEM_FAILURE]: 'try_it.auth.exit_system_failure',
  [AuthTerminalCause.LLM_EXIT]: 'try_it.auth.exit_llm_exit',
};

const RESULT_TRANSLATION_KEY: Partial<Record<AuthValidationResult, string>> = {
  [AuthValidationResult.VALID]: 'try_it.auth.result.valid',
  [AuthValidationResult.INVALID]: 'try_it.auth.result.invalid',
  [AuthValidationResult.ERROR]: 'try_it.auth.result.error',
};

const STEP_TRANSLATION_KEY: Partial<Record<AuthStepType, string>> = {
  [AuthStepType.IDENTIFICATION]: 'try_it.auth.identification',
  [AuthStepType.VERIFICATION]: 'try_it.auth.verification',
};

const VALIDATION_METHOD_TRANSLATION_KEY: Partial<Record<AuthValidationMethod, string>> = {
  [AuthValidationMethod.EXACT_MATCH]: 'try_it.auth.method_label.exact_match',
  [AuthValidationMethod.FUZZY_MATCH]: 'try_it.auth.method_label.fuzzy_match',
  [AuthValidationMethod.CONTAINS]: 'try_it.auth.method_label.contains',
  [AuthValidationMethod.STARTS_WITH]: 'try_it.auth.method_label.starts_with',
  [AuthValidationMethod.ENDS_WITH]: 'try_it.auth.method_label.ends_with',
  [AuthValidationMethod.GUIDELINE]: 'try_it.auth.method_label.guideline',
  [AuthValidationMethod.WEBHOOK]: 'try_it.auth.method_label.webhook',
  [AuthValidationMethod.FORMAT]: 'try_it.auth.method_label.format',
};

// For partial-match validators the user input is a fragment of the ground truth,
// so "Expected value" reads wrong (the input is not expected to equal the truth).
// Each method picks a label that describes the comparison: e.g. for `contains`,
// the row reads "Contained in: ΧΝΖ1371".
const GROUND_TRUTH_LABEL_KEY: Partial<Record<AuthValidationMethod, string>> = {
  [AuthValidationMethod.CONTAINS]: 'try_it.auth.ground_truth_label.contained_in',
  [AuthValidationMethod.STARTS_WITH]: 'try_it.auth.ground_truth_label.at_start_of',
  [AuthValidationMethod.ENDS_WITH]: 'try_it.auth.ground_truth_label.at_end_of',
};

const toneFromResult = (result?: AuthValidationResult): Tone => {
  if (result === AuthValidationResult.VALID) {
    return 'success';
  }
  if (result === AuthValidationResult.INVALID) {
    return 'warning';
  }
  if (result === AuthValidationResult.ERROR) {
    return 'destructive';
  }
  return 'neutral';
};

const toneFromCause = (cause?: AuthTerminalCause): Tone => {
  // THRESHOLD_MET is the successful terminal cause (green). A guideline-based
  // exit (LLM_EXIT) is an intentional opt-out / routed-out path, not a failure,
  // so it reads as warning (amber) rather than destructive. Hard failures
  // (max attempts, timeout, system failure) stay destructive (red).
  if (cause === AuthTerminalCause.THRESHOLD_MET) {
    return 'success';
  }
  if (cause === AuthTerminalCause.LLM_EXIT) {
    return 'warning';
  }
  return 'destructive';
};

const toneClassName: Record<Tone, string> = {
  neutral: 'text-muted-foreground',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

// ShieldCheck is reserved for the successful exit (THRESHOLD_MET); ShieldOff
// marks a guideline-based exit (the user opted out / was routed out — not a
// failure); ShieldX marks hard failures (max attempts, timeout, system error).
// Other events use the plain shield — the icon's tone color already signals
// validation outcome.
const IconForEvent = ({
  event,
  terminalCause,
  hasWebhook,
  className,
}: {
  event?: AuthEventType;
  terminalCause?: AuthTerminalCause;
  hasWebhook?: boolean;
  className?: string;
}) => {
  const props = { size: 16, className };
  switch (event) {
    case AuthEventType.AUTH_PRE_ENTER_WEBHOOK:
      return <WebhookIcon {...props} />;
    case AuthEventType.AUTH_VALIDATE_INPUT:
      return hasWebhook ? <WebhookIcon {...props} /> : <ShieldIcon {...props} />;
    case AuthEventType.AUTH_EXIT: {
      if (terminalCause === AuthTerminalCause.THRESHOLD_MET) {
        return <ShieldCheckIcon {...props} />;
      }
      if (terminalCause === AuthTerminalCause.LLM_EXIT) {
        return <ShieldOffIcon {...props} />;
      }
      return <ShieldXIcon {...props} />;
    }
    case AuthEventType.AUTH_VALIDATOR_SETUP_ERROR:
      return <ShieldAlertIcon {...props} />;
    case AuthEventType.AUTH_STEP_ENTERED:
    default:
      return <ShieldIcon {...props} />;
  }
};

const CodePill = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex items-center rounded bg-muted/50 px-1.5 py-0.5 font-mono text-xs text-foreground">
    {children}
  </span>
);

const contextChangeLabel = (t: TFunction, change: ContextChangeType): string => {
  if (change === ContextChangeType.ADDED) {
    return t('try_it.details.added');
  }
  if (change === ContextChangeType.REMOVED) {
    return t('activityLogs.removed');
  }
  return t('activityLogs.updated');
};

const STEP_QUERY_PARAM: Partial<Record<AuthStepType, AuthStepKey>> = {
  [AuthStepType.IDENTIFICATION]: 'identification',
  [AuthStepType.VERIFICATION]: 'verification',
};

// Pill button that closes the TryIt modal and deep-links elsewhere.
// `buildPath` runs at click time with the current brainId/slug; both
// WebhookLink and AuthQuestionLink delegate URL construction to it so this
// component owns the styling, modal close, and brainId guard once.
const LinkPill = ({
  buildPath,
  children,
}: {
  // `null` signals the pill is non-navigable (e.g. the target action isn't
  // resolvable). The pill still renders so callers don't have to special-case
  // styling, but click is a no-op.
  buildPath: (brainId: string, slug: string) => string | null;
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);

  const onClick = () => {
    if (!brainId) {
      return;
    }
    const path = buildPath(brainId, slug);
    if (!path) {
      return;
    }
    navigate(path);
    dispatch(popModal());
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex cursor-pointer items-center rounded bg-muted/50 px-1.5 py-0.5 font-mono text-xs text-primary hover:underline"
    >
      {children}
    </button>
  );
};

const WebhookLink = ({ webhookId }: { webhookId: string }) => {
  const brainId = useSelector(selectBrainId);
  const { webhook } = useWebhooks(brainId, webhookId);
  return (
    <LinkPill
      buildPath={(brain, slug) => `/${slug}/ai-agents/${brain}/workflows/webhooks/${webhookId}`}
    >
      {webhook?.name || webhookId}
    </LinkPill>
  );
};

// Deep-links into the dialog that hosts the auth sub_agent action via
// ?action=&step=<identification|verification>&question=<save_as>. The
// receiving Dialog page selects the action + auth panel; AuthPageStepPanel
// scrolls + briefly highlights the matching question card.
//
// `actionId` is guaranteed populated on every AUTH debug step by the dialog
// service. `dialogId` may legitimately resolve to null while the dialogs
// cache is still loading — in that case `LinkPill` renders the same button
// styling but `buildPath` returns null and the click is a no-op.
const AuthQuestionLink = ({
  questionKey,
  step,
  actionId,
  children,
}: {
  questionKey: string;
  step?: AuthStepType;
  actionId: string;
  children: ReactNode;
}) => {
  const brainId = useSelector(selectBrainId);
  const { dialogs } = useDialogs(brainId);

  // getActionsSelector flattens actions across node / condition-branch /
  // requisite containers, so this resolves the auth action regardless of where
  // it lives in the node (a plain `node.actions` scan misses condition
  // branches).
  const dialogId = useMemo(
    () => getActionsSelector(dialogs)?.find((a) => a.action_id === actionId)?.dialog_id ?? null,
    [actionId, dialogs]
  );

  const buildPath = (brain: string, slug: string) => {
    if (!dialogId) {
      return null;
    }
    const stepParam = step ? STEP_QUERY_PARAM[step] : undefined;
    const search = new URLSearchParams();
    if (stepParam) {
      search.set('step', stepParam);
    }
    if (questionKey) {
      search.set('question', questionKey);
    }
    search.set('action', actionId);
    const qs = search.toString();
    return `/${slug}/ai-agents/${brain}/workflows/dialogs/${dialogId}${qs ? `?${qs}` : ''}`;
  };
  return <LinkPill buildPath={buildPath}>{children}</LinkPill>;
};

export const AuthStep = ({ step }: AuthStepProps) => {
  const { t } = useTranslation();
  const auth: AuthDebugStep | undefined = step.auth;

  if (!auth) {
    return null;
  }

  const event = auth.event;
  const stepKey = auth.step ? STEP_TRANSLATION_KEY[auth.step] : '';
  const stepLabel = stepKey ? t(stepKey) : '';

  const tone: Tone = (() => {
    if (event === AuthEventType.AUTH_VALIDATE_INPUT) {
      return toneFromResult(auth.validation_result);
    }
    if (event === AuthEventType.AUTH_PRE_ENTER_WEBHOOK) {
      return auth.validation_result === AuthValidationResult.ERROR ? 'destructive' : 'neutral';
    }
    if (event === AuthEventType.AUTH_EXIT) {
      return toneFromCause(auth.terminal_cause);
    }
    if (event === AuthEventType.AUTH_VALIDATOR_SETUP_ERROR) {
      return 'destructive';
    }
    return 'neutral';
  })();

  const renderBody = (): ReactNode => {
    switch (event) {
      case AuthEventType.AUTH_STEP_ENTERED:
        return t('try_it.auth.step_entered', { 0: stepLabel });
      case AuthEventType.AUTH_PRE_ENTER_WEBHOOK:
        return (
          <>
            {t('try_it.auth.pre_enter_webhook_prefix')}{' '}
            {step.webhook_id ? <WebhookLink webhookId={step.webhook_id} /> : null}
          </>
        );
      case AuthEventType.AUTH_VALIDATE_INPUT:
        return (
          <>
            {t('try_it.auth.validated_input_prefix')}{' '}
            <AuthQuestionLink
              questionKey={auth.question_key ?? ''}
              step={auth.step}
              actionId={step.action_id ?? ''}
            >
              {auth.question_key}
            </AuthQuestionLink>
            {': '}
            <CodePill>{auth.input_value}</CodePill>
            {step.webhook_id ? (
              <>
                {' '}
                {t('try_it.auth.via')} <WebhookLink webhookId={step.webhook_id} />
              </>
            ) : null}
          </>
        );
      case AuthEventType.AUTH_EXIT: {
        // THRESHOLD_MET → "completed the {step} step" (works for both
        // intermediate and final). Other causes → per-cause exit copy.
        if (auth.terminal_cause === AuthTerminalCause.THRESHOLD_MET) {
          return t('try_it.auth.step_completed', { 0: stepLabel });
        }
        const exitKey = auth.terminal_cause ? EXIT_TRANSLATION_KEY[auth.terminal_cause] : undefined;
        return exitKey ? t(exitKey) : null;
      }
      case AuthEventType.AUTH_VALIDATOR_SETUP_ERROR:
        // The question pill deep-links to the offending question's config.
        // When the failure isn't a missing-var case (rare: webhook id not
        // found, unknown method), the trailing clause is omitted.
        return (
          <>
            {t('try_it.auth.validator_setup_error')}{' '}
            <AuthQuestionLink
              questionKey={auth.question_key ?? ''}
              step={auth.step}
              actionId={step.action_id ?? ''}
            >
              {auth.question_key}
            </AuthQuestionLink>
            {auth.missing_context_variable ? (
              <>
                {' — '}
                {t('try_it.auth.missing_context_variable')}{' '}
                <CodePill>{auth.missing_context_variable}</CodePill>{' '}
                {t('try_it.auth.missing_context_variable_suffix')}
              </>
            ) : null}
            {auth.match_size && auth.ground_truth_length ? (
              <>
                {' — '}
                {t('try_it.auth.match_size_exceeds_ground_truth', {
                  0: auth.match_size,
                  1: auth.ground_truth_length,
                })}
              </>
            ) : null}
          </>
        );
      default:
        return null;
    }
  };

  // Context-variable updates returned by a webhook. The pre-enter webhook
  // populates ground truth before a step; a validation webhook can also return
  // ground truth alongside `valid` on a successful identification (MOV-16933).
  // Both surface the same "N context variables updated" accordion. Non-webhook
  // validators never carry context_changes, so presence alone gates rendering.
  const renderContextChanges = (): ReactNode => {
    const contextChanges = step.context_changes
      ? Object.entries(step.context_changes).map(([key, change]) => ({
          key,
          label: contextChangeLabel(t, change),
        }))
      : [];
    if (contextChanges.length === 0) {
      return null;
    }
    return (
      <Accordion type="single" collapsible className="mt-1">
        <AccordionItem value="context-changes" className="border-none">
          <AccordionTrigger className="py-1 text-xs font-normal text-muted-foreground hover:no-underline">
            {t('try_it.auth.context_variables_updated', {
              count: contextChanges.length,
              0: contextChanges.length,
            })}
          </AccordionTrigger>
          <AccordionContent className="pb-1 text-xs text-muted-foreground">
            <ul className="ml-4 list-disc">
              {contextChanges.map((c) => (
                <li key={c.key}>
                  {c.key}: {c.label}
                </li>
              ))}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  };

  // Pre-enter webhook and validate_input have distinct detail layouts; each
  // event type renders its own block. Duration is a plain line (single piece
  // of meta info), bullets are reserved for multi-field detail (expected
  // value, score). Both surface webhook context updates via renderContextChanges.
  const renderDetails = (): ReactNode => {
    if (event === AuthEventType.AUTH_PRE_ENTER_WEBHOOK) {
      const contextChanges = renderContextChanges();
      if (step.duration === undefined && contextChanges === null) {
        return null;
      }
      return (
        <div className="text-xs break-words text-muted-foreground">
          {step.duration !== undefined && (
            <div className="mt-0.5">
              {t('try_it.auth.duration_label')}: {step.duration}ms
            </div>
          )}
          {contextChanges}
        </div>
      );
    }

    if (event !== AuthEventType.AUTH_VALIDATE_INPUT) {
      return null;
    }

    const fields: { label: string; value: string }[] = [];
    if (auth.ground_truth) {
      const groundTruthLabelKey = auth.validation_method
        ? GROUND_TRUTH_LABEL_KEY[auth.validation_method]
        : undefined;
      fields.push({
        label: t(groundTruthLabelKey ?? 'try_it.auth.expected_value'),
        value: auth.ground_truth,
      });
    }
    if (auth.fuzzy_score !== undefined) {
      fields.push({
        label: t('try_it.auth.score'),
        value: t('try_it.auth.fuzzy_score_value', { 0: auth.fuzzy_score }),
      });
    }
    // Partial-match only (zero/absent otherwise): explains why a matching but
    // too-short answer was rejected.
    if (auth.match_size) {
      fields.push({ label: t('try_it.auth.match_size'), value: String(auth.match_size) });
    }

    const methodKey = auth.validation_method
      ? VALIDATION_METHOD_TRANSLATION_KEY[auth.validation_method]
      : '';
    const methodLabel = methodKey ? t(methodKey) : t('try_it.auth.method_label.unknown');
    const resultKey = auth.validation_result
      ? RESULT_TRANSLATION_KEY[auth.validation_result]
      : undefined;
    const resultLabel = resultKey ? t(resultKey) : '';
    const heading = resultLabel
      ? t('try_it.auth.heading_method_result', { 0: methodLabel, 1: resultLabel })
      : methodLabel;
    const durationMs = step.webhook_id && step.duration !== undefined ? step.duration : undefined;
    // A webhook-backed validation may also return ground-truth context updates
    // (MOV-16933); render them under the same accordion as the pre-enter webhook.
    const contextChanges = renderContextChanges();

    if (
      !resultLabel &&
      fields.length === 0 &&
      durationMs === undefined &&
      contextChanges === null
    ) {
      return null;
    }

    return (
      <div className="text-xs break-words text-muted-foreground">
        {heading && <div className="font-semibold">{heading}</div>}
        {durationMs !== undefined && (
          <div className="mt-0.5">
            {t('try_it.auth.duration_label')}: {durationMs}ms
          </div>
        )}
        {fields.length > 0 && (
          <ul className="mt-0.5 ml-4 list-disc">
            {fields.map((f) => (
              <li key={f.label}>
                {f.label}: {f.value}
              </li>
            ))}
          </ul>
        )}
        {contextChanges}
      </div>
    );
  };

  return (
    <div className="flex w-full min-w-0 items-start gap-4">
      <IconForEvent
        event={event}
        terminalCause={auth.terminal_cause}
        hasWebhook={Boolean(step.webhook_id)}
        className={cn('mt-0.5 shrink-0', toneClassName[tone])}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-sm leading-5 break-words text-muted-foreground">{renderBody()}</span>
        {renderDetails()}
      </div>
    </div>
  );
};
